import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { InfoFooter, InfoHeader } from "../../components/InfoLayout";
import usePageMeta from "../../hooks/usePageMeta";
import aboutCss from "../../styles/about-sbr.css?raw";
import infoCss from "../../styles/info.css?raw";

const PLATE = "SBR 26 UK";
const STORAGE_KEY = "sbr-about-seen-v2";

const LINES = [
  "Hello. Thanks for stopping by.",
  "We check cars by registration.",
  "Service history, finance, write-offs, stolen markers.",
  "Then we put it in plain English.",
];

const CHECKS = [
  {
    id: "service",
    label: "Service history",
    blurb: "Dates, mileage and garage notes from manufacturer records, when those records exist.",
  },
  {
    id: "finance",
    label: "Outstanding finance",
    blurb: "Whether money is still owed on the car that could follow the next owner.",
  },
  {
    id: "writeoff",
    label: "Write-off status",
    blurb: "If an insurer recorded the car as a total loss, and which category it got.",
  },
  {
    id: "stolen",
    label: "Stolen markers",
    blurb: "Whether the registration is flagged as stolen in the databases we check.",
  },
];

function prefersReducedMotion() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function Welcome({ onDone }) {
  const [visibleChars, setVisibleChars] = useState(0);
  const [lineIndex, setLineIndex] = useState(0);
  const timers = useRef([]);
  const chars = useMemo(() => PLATE.split(""), []);

  useEffect(() => {
    const clear = () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    };

    if (prefersReducedMotion()) {
      setVisibleChars(chars.length);
      setLineIndex(LINES.length - 1);
      timers.current.push(setTimeout(onDone, 600));
      return clear;
    }

    chars.forEach((_, i) => {
      timers.current.push(setTimeout(() => setVisibleChars(i + 1), 280 + i * 70));
    });

    LINES.forEach((_, i) => {
      timers.current.push(setTimeout(() => setLineIndex(i), 600 + i * 2400));
    });

    timers.current.push(setTimeout(onDone, 600 + LINES.length * 2400 + 1200));
    return clear;
  }, [chars, onDone]);

  return (
    <section className="sbr-welcome" aria-label="Welcome">
      <button type="button" className="sbr-welcome-skip" onClick={onDone}>
        Skip
      </button>
      <div className="sbr-welcome-box">
        <div className="sbr-plate" aria-hidden="true">
          <div className="sbr-plate-eu">
            <span>★</span>
            <span>GB</span>
          </div>
          <div className="sbr-plate-chars">
            {chars.map((ch, i) => (
              <span key={`${ch}-${i}`} className={i < visibleChars ? "is-on" : ""}>
                {ch === " " ? "\u00a0" : ch}
              </span>
            ))}
          </div>
        </div>
        <p className="sbr-welcome-line" aria-live="polite">
          {LINES[lineIndex]}
        </p>
        <button type="button" className="sbr-welcome-continue" onClick={onDone}>
          Continue
        </button>
      </div>
    </section>
  );
}

function AboutBody({ onReplay }) {
  const [open, setOpen] = useState("service");

  return (
    <div className="sbr-about-body">
      <section className="sbr-about-hero wrap">
        <p className="sbr-kicker">About</p>
        <h1>
          <span>Check the risks.</span>
          <span className="sbr-soft">Before you buy.</span>
        </h1>
        <p className="sbr-lead">
          ServiceByReg helps you check a car by its registration before you buy. We pull the records
          that matter into one report you can actually read.
        </p>
        <button type="button" className="sbr-text-btn" onClick={onReplay}>
          See the short welcome again
        </button>
      </section>

      <section className="wrap sbr-about-main">
        <h2>What we look for</h2>
        <p className="sbr-section-copy">Tap a row. That is what sits behind a typical report.</p>
        <ul className="sbr-check-list">
          {CHECKS.map((item) => {
            const isOpen = open === item.id;
            return (
              <li key={item.id}>
                <button
                  type="button"
                  className={`sbr-check-row${isOpen ? " is-open" : ""}`}
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? "" : item.id)}
                >
                  <span className="sbr-check-label">{item.label}</span>
                  <span className="sbr-check-toggle" aria-hidden="true">
                    {isOpen ? "−" : "+"}
                  </span>
                </button>
                {isOpen && <p className="sbr-check-detail">{item.blurb}</p>}
              </li>
            );
          })}
        </ul>

        <h2>How we work</h2>
        <div className="sbr-points">
          <div>
            <h3>We start with the registration</h3>
            <p>
              Most buyers already have a plate. Type it in and we check the sources that sit behind a
              proper history report.
            </p>
          </div>
          <div>
            <h3>We do not invent missing data</h3>
            <p>
              If a source has nothing for that car, we say so. A blank section means the record was
              not there, not that we skipped the check.
            </p>
          </div>
          <div>
            <h3>You pay once</h3>
            <p>
              Reports are one-time purchases. Optional V5C monitoring covers the period you choose.
              No subscription for a single used-car decision.
            </p>
          </div>
        </div>

        <div className="sbr-note">
          <p>
            A report helps you ask better questions. It does not replace looking at the car, checking
            the V5C, or getting advice when you need it. We are not DVLA or DVSA.
          </p>
        </div>

        <div className="sbr-actions">
          <a className="sbr-btn" href="/">
            Check a registration
          </a>
          <a className="sbr-btn-link" href="/contact">
            Contact us
          </a>
        </div>
      </section>
    </div>
  );
}

export default function ServiceByRegAbout({ brand }) {
  usePageMeta({
    title: "About ServiceByReg",
    description:
      "ServiceByReg helps UK drivers check a car by registration: service history, finance, write-offs and stolen markers in one clear report.",
  });

  const [showWelcome, setShowWelcome] = useState(() => {
    if (typeof window === "undefined") return true;
    if (prefersReducedMotion()) return false;
    try {
      return sessionStorage.getItem(STORAGE_KEY) !== "1";
    } catch {
      return true;
    }
  });

  const finishWelcome = useCallback(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {
      /* ignore */
    }
    setShowWelcome(false);
  }, []);

  const replay = () => {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    setShowWelcome(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      <style>{infoCss}</style>
      <style>{aboutCss}</style>
      <div className="sbr-about">
        <InfoHeader brand={brand} />
        <main>
          {showWelcome ? <Welcome onDone={finishWelcome} /> : <AboutBody onReplay={replay} />}
        </main>
        {!showWelcome && <InfoFooter brand={brand} />}
      </div>
    </>
  );
}
